import { Action } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Coins, Plus, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	getVendorSpendQueryOptions,
	listFinancialCategoriesQueryOptions,
	listVendorsQueryOptions,
	type Vendor,
} from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney } from "@/utils/financial";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../-components/FinancialHeaderActions";
import { PeriodSelector } from "../-components/PeriodSelector";
import { getPeriodRange, type PeriodPreset } from "../-components/period";
import { StatCard } from "../-components/StatCard";
import { VendorFormDialog } from "./-components/VendorFormDialog";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/vendors/",
)({
	component: VendorsPage,
});

/** A merged row: a registered vendor (editable) or a deleted vendor still bearing spend. */
type VendorRow = {
	id: string;
	name: string;
	document: string | null;
	defaultCategoryId: string | null;
	total: string;
	transactionCount: number;
	/** The editable register entry, or null for a soft-deleted vendor with history. */
	vendor: Vendor | null;
};

function VendorsPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const scope = useFarmScope();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [preset, setPreset] = useState<PeriodPreset>("thisMonth");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<Vendor | null>(null);

	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;
	const period = useMemo(() => getPeriodRange(preset), [preset]);

	const { data: vendors, isLoading: vendorsLoading } = useQuery(
		listVendorsQueryOptions(orgId),
	);
	const { data: spend, isLoading: spendLoading } = useQuery(
		getVendorSpendQueryOptions(orgId, {
			periodStart: period.periodStart,
			periodEnd: period.periodEnd,
			...(establishmentId ? { establishmentId } : {}),
		}),
	);
	const { data: categories } = useQuery(
		listFinancialCategoriesQueryOptions(orgId),
	);

	const currency = spend?.currency ?? "BRL";
	const activeCategories = useMemo(
		() => (categories ?? []).filter((c) => c.isActive),
		[categories],
	);
	const categoryLabel = useMemo(() => {
		const map = new Map<string, string>();
		for (const c of categories ?? []) map.set(c.id, c.label);
		return map;
	}, [categories]);

	// Merge the register (editable, active) with the spend report (which also
	// carries soft-deleted vendors that still hold historical spend), so the
	// table both manages vendors and reconciles spend with the transactions list.
	const rows = useMemo<VendorRow[]>(() => {
		const spendById = new Map(
			(spend?.vendors ?? []).map((s) => [s.vendorId, s]),
		);
		const result: VendorRow[] = [];
		const seen = new Set<string>();

		for (const vendor of vendors ?? []) {
			const s = spendById.get(vendor.id);
			seen.add(vendor.id);
			result.push({
				id: vendor.id,
				name: vendor.name,
				document: vendor.document,
				defaultCategoryId: vendor.defaultCategoryId,
				total: s?.total ?? "0.00",
				transactionCount: s?.transactionCount ?? 0,
				vendor,
			});
		}
		// Soft-deleted vendors with spend in the period — read-only, but counted.
		for (const s of spend?.vendors ?? []) {
			if (seen.has(s.vendorId)) continue;
			result.push({
				id: s.vendorId,
				name: s.vendorName,
				document: s.vendorDocument,
				defaultCategoryId: null,
				total: s.total,
				transactionCount: s.transactionCount,
				vendor: null,
			});
		}
		return result.sort(
			(a, b) => Number.parseFloat(b.total) - Number.parseFloat(a.total),
		);
	}, [vendors, spend]);

	const isLoading = vendorsLoading || spendLoading;

	const openCreate = () => {
		setEditing(null);
		setDialogOpen(true);
	};
	const openEdit = (vendor: Vendor) => {
		setEditing(vendor);
		setDialogOpen(true);
	};

	return (
		<div className="flex flex-col gap-4">
			{canManage && (
				<FinancialHeaderActions>
					<HeaderActionButton
						icon={Plus}
						label={t("financial.vendors.newVendor")}
						shortLabel={t("financial.vendors.newVendorShort")}
						onClick={openCreate}
					/>
				</FinancialHeaderActions>
			)}

			<PeriodSelector value={preset} onChange={setPreset} />

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<StatCard
					label={t("financial.vendors.summary.totalSpend")}
					value={formatMoney(spend?.total ?? "0", currency)}
					icon={Coins}
				/>
				<StatCard
					label={t("financial.vendors.summary.supplierCount")}
					value={String(spend?.vendors.length ?? 0)}
					icon={Truck}
				/>
			</div>

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					{isLoading ? (
						<div className="flex justify-center py-16">
							<LoadingSpinner />
						</div>
					) : rows.length === 0 ? (
						<div className="py-12 text-center">
							<p className="font-medium">
								{t("financial.vendors.empty.title")}
							</p>
							<p className="mt-1 text-sm text-base-content/60">
								{t("financial.vendors.empty.description")}
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("financial.vendors.columns.vendor")}</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.vendors.columns.defaultCategory")}
									</TableHead>
									<TableHead className="text-right hidden md:table-cell">
										{t("financial.vendors.columns.transactions")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.vendors.columns.spend")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((row) => {
									const editable = canManage && row.vendor !== null;
									return (
										<TableRow
											key={row.id}
											className={editable ? "cursor-pointer" : undefined}
											onClick={
												editable && row.vendor
													? () => openEdit(row.vendor as Vendor)
													: undefined
											}
										>
											<TableCell>
												<span className="font-medium">{row.name}</span>
												{row.vendor === null && (
													<Badge variant="outline" className="ml-2">
														{t("financial.vendors.removedBadge")}
													</Badge>
												)}
												{row.document && (
													<span className="block text-xs text-base-content/60">
														{row.document}
													</span>
												)}
											</TableCell>
											<TableCell className="hidden sm:table-cell text-sm text-base-content/70">
												{row.defaultCategoryId
													? (categoryLabel.get(row.defaultCategoryId) ?? "—")
													: "—"}
											</TableCell>
											<TableCell className="text-right hidden md:table-cell">
												{row.transactionCount}
											</TableCell>
											<TableCell className="text-right font-medium">
												{formatMoney(row.total, currency)}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</div>
			</div>

			{canManage && (
				<VendorFormDialog
					orgId={orgId}
					open={dialogOpen}
					onOpenChange={setDialogOpen}
					vendor={editing}
					categories={activeCategories}
				/>
			)}
		</div>
	);
}
